import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Upload from 'lucide-react/dist/esm/icons/upload';
import X from 'lucide-react/dist/esm/icons/x';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

export type ScriptFormato = 'corto' | 'largo' | 'serie';
export type ScriptTipo = 'ficcion' | 'documental';
export type ScriptSourceDocType = 'guion' | 'tratamiento';

export interface StartAnalysisOptions {
  formato: ScriptFormato;
  tipo: ScriptTipo;
  sourceDocType: ScriptSourceDocType;
  /** Solo en serie: capitulos de la temporada que se suben. El motor construye el
   *  presupuesto de UN capitulo y lo multiplica por este numero. */
  numCapitulos?: number;
}

/** Capitulos por defecto de una temporada corta espanola. */
const NUM_CAPITULOS_DEFECTO = 8;
/** Limites de la columna analysis_jobs.num_capitulos (constraint 1..30). */
const NUM_CAPITULOS_MIN = 1;
const NUM_CAPITULOS_MAX = 30;

interface ScriptUploadProps {
  onFileSelected: (file: File) => void;
  onStartAnalysis: (opts: StartAnalysisOptions) => void;
  selectedFile: File | null;
  isStarting: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ScriptUpload({ onFileSelected, onStartAnalysis, selectedFile, isStarting }: ScriptUploadProps) {
  const [formato, setFormato] = useState<ScriptFormato>('largo');
  const [tipo, setTipo] = useState<ScriptTipo>('ficcion');
  const [numCapitulos, setNumCapitulos] = useState<number>(NUM_CAPITULOS_DEFECTO);
  const [sourceDocType, setSourceDocType] = useState<ScriptSourceDocType>('guion');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/xml': ['.fdx'],
      'text/plain': ['.fountain'],
    },
    multiple: false,
    onDrop: (accepted) => {
      if (accepted.length > 0) {
        onFileSelected(accepted[0]);
      }
    },
  });

  return (
    <div className="space-y-6">
      <div
        {...getRootProps()}
        className={cn(
          'border-dashed border-2 border-muted-foreground/25 rounded-lg p-12 text-center cursor-pointer transition-colors',
          isDragActive && 'border-primary bg-primary/5'
        )}
        role="button"
        aria-label="Subir archivo de guion"
      >
        <input {...getInputProps()} />
        {selectedFile ? (
          <div className="flex items-center justify-center gap-3">
            <div className="text-sm">
              <span className="font-medium">{selectedFile.name}</span>
              <span className="text-muted-foreground ml-2">
                ({formatFileSize(selectedFile.size)})
              </span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              aria-label="Eliminar archivo"
              onClick={(e) => {
                e.stopPropagation();
                onFileSelected(null as unknown as File);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Arrastra tu guion aqui o haz clic para seleccionar
            </p>
            <p className="text-xs text-muted-foreground">
              PDF, Final Draft (.fdx), Fountain (.fountain)
            </p>
          </div>
        )}
      </div>

      <div className="max-w-md mx-auto space-y-3">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">¿Qué subes?</label>
          <Select value={sourceDocType} onValueChange={(v) => setSourceDocType(v as ScriptSourceDocType)}>
            <SelectTrigger aria-label="Tipo de documento">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="guion">Guion (con escenas)</SelectItem>
              <SelectItem value="tratamiento">Tratamiento (prosa → asistente de escritura)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Formato</label>
          <Select value={formato} onValueChange={(v) => setFormato(v as ScriptFormato)}>
            <SelectTrigger aria-label="Formato">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="largo">Largometraje</SelectItem>
              <SelectItem value="corto">Cortometraje</SelectItem>
              <SelectItem value="serie">Serie</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Tipo</label>
          <Select value={tipo} onValueChange={(v) => setTipo(v as ScriptTipo)}>
            <SelectTrigger aria-label="Tipo">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ficcion">Ficcion</SelectItem>
              <SelectItem value="documental">Documental</SelectItem>
            </SelectContent>
          </Select>
        </div>
        </div>

        {/* Numero de capitulos: solo lo sabe quien sube el guion, y el motor lo
            necesita para multiplicar el presupuesto del capitulo por la temporada. */}
        {formato === 'serie' && (
          <div>
            <label className="text-xs text-muted-foreground mb-1 block" htmlFor="num-capitulos">
              Capitulos de la temporada
            </label>
            <input
              id="num-capitulos"
              type="number"
              min={NUM_CAPITULOS_MIN}
              max={NUM_CAPITULOS_MAX}
              step={1}
              value={numCapitulos}
              aria-label="Numero de capitulos"
              onChange={(e) => {
                const n = Math.round(Number(e.target.value));
                if (!Number.isFinite(n)) return;
                setNumCapitulos(Math.min(NUM_CAPITULOS_MAX, Math.max(NUM_CAPITULOS_MIN, n)));
              }}
              className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Pon los capitulos que subes, no los que tendra la temporada. El presupuesto se
              calcula por capitulo y se multiplica por este numero.
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-center">
        <Button
          onClick={() => onStartAnalysis({
            formato,
            tipo,
            sourceDocType,
            ...(formato === 'serie' ? { numCapitulos } : {}),
          })}
          disabled={!selectedFile || isStarting}
        >
          {isStarting ? 'Iniciando...' : 'Iniciar Analisis'}
        </Button>
      </div>
    </div>
  );
}
